import * as i0 from '@angular/core';
import { forwardRef, PLATFORM_ID, Component, Inject, Input, Output, HostListener, ContentChildren, NgModule } from '@angular/core';
import DxNumberBox from 'devextreme/ui/number_box';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i1 from 'devextreme-angular/core';
import { DxComponent, DxTemplateHost, WatcherHelper, NestedOptionHost, IterableDifferHelper, DxIntegrationModule, DxTemplateModule } from 'devextreme-angular/core';
import { DxiButtonComponent, DxiButtonModule, DxoOptionsModule, DxoFormatModule } from 'devextreme-angular/ui/nested';

/*!
 * devextreme-angular
 * Version: 24.1.3
 * Build date: Tue Jun 11 2024
 *
 * Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
/* tslint:disable:max-line-length */
const CUSTOM_VALUE_ACCESSOR_PROVIDER = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DxNumberBoxComponent),
  multi: true
};
/**
 * The NumberBox is a UI component that displays a numeric value and allows a user to modify it by typing in a value, and incrementing or decrementing it using the keyboard or mouse.

 */
let DxNumberBoxComponent = /*#__PURE__*/(() => {
  class DxNumberBoxComponent extends DxComponent {
    _watcherHelper;
    _idh;
    instance = null;
    /**
     * Specifies the shortcut key that sets focus on the UI component.
    
     */
    get accessKey() {
      return this._getOption('accessKey');
    }
    set accessKey(value) {
      this._setOption('accessKey', value);
    }
    /**
     * Specifies whether the UI component changes its visual state as a result of user interaction.
    
     */
    get activeStateEnabled() {
      return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value) {
      this._setOption('activeStateEnabled', value);
    }
    /**
     * Allows you to add custom buttons to the input text field.
    
     */
    get buttons() {
      return this._getOption('buttons');
    }
    set buttons(value) {
      this._setOption('buttons', value);
    }
    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    get disabled() {
      return this._getOption('disabled');
    }
    set disabled(value) {
      this._setOption('disabled', value);
    }
    /**
     * Specifies the global attributes to be attached to the UI component&apos;s container element.
    
     */
    get elementAttr() {
      return this._getOption('elementAttr');
    }
    set elementAttr(value) {
      this._setOption('elementAttr', value);
    }
    /**
     * Specifies whether the UI component can be focused using keyboard navigation.
    
     */
    get focusStateEnabled() {
      return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value) {
      this._setOption('focusStateEnabled', value);
    }
    /**
     * Specifies the value&apos;s display format and controls user input accordingly.
    
     */
    get format() {
      return this._getOption('format');
    }
    set format(value) {
      this._setOption('format', value);
    }
    /**
     * Specifies the UI component&apos;s height.
    
     */
    get height() {
      return this._getOption('height');
    }
    set height(value) {
      this._setOption('height', value);
    }
    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    get hint() {
      return this._getOption('hint');
    }
    set hint(value) {
      this._setOption('hint', value);
    }
    /**
     * Specifies whether the UI component changes its state when a user pauses on it.
    
     */
    get hoverStateEnabled() {
      return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value) {
      this._setOption('hoverStateEnabled', value);
    }
    /**
     * Specifies the attributes to be passed on to the underlying HTML element.
    
     */
    get inputAttr() {
      return this._getOption('inputAttr');
    }
    set inputAttr(value) {
      this._setOption('inputAttr', value);
    }
    /**
     * Specifies the text of the message displayed if the specified value is not a number.
    
     */
    get invalidValueMessage() {
      return this._getOption('invalidValueMessage');
    }
    set invalidValueMessage(value) {
      this._setOption('invalidValueMessage', value);
    }
    /**
     * Specifies whether the component&apos;s current value differs from the initial value.
    
     */
    get isDirty() {
      return this._getOption('isDirty');
    }
    set isDirty(value) {
      this._setOption('isDirty', value);
    }
    /**
     * Specifies or indicates whether the editor&apos;s value is valid.
    
     */
    get isValid() {
      return this._getOption('isValid');
    }
    set isValid(value) {
      this._setOption('isValid', value);
    }
    /**
     * Specifies a text string used to annotate the editor&apos;s value.
    
     */
    get label() {
      return this._getOption('label');
    }
    set label(value) {
      this._setOption('label', value);
    }
    /**
     * Specifies the label&apos;s display mode.
    
     */
    get labelMode() {
      return this._getOption('labelMode');
    }
    set labelMode(value) {
      this._setOption('labelMode', value);
    }
    /**
     * The maximum value accepted by the number box.
    
     */
    get max() {
      return this._getOption('max');
    }
    set max(value) {
      this._setOption('max', value);
    }
    /**
     * The minimum value accepted by the number box.
    
     */
    get min() {
      return this._getOption('min');
    }
    set min(value) {
      this._setOption('min', value);
    }
    /**
     * Specifies the value to be passed to the type attribute of the underlying `` element.
    
     */
    get mode() {
      return this._getOption('mode');
    }
    set mode(value) {
      this._setOption('mode', value);
    }
    /**
     * The value to be assigned to the `name` attribute of the underlying HTML element.
    
     */
    get name() {
      return this._getOption('name');
    }
    set name(value) {
      this._setOption('name', value);
    }
    /**
     * Specifies a text string displayed when the editor&apos;s value is empty.
    
     */
    get placeholder() {
      return this._getOption('placeholder');
    }
    set placeholder(value) {
      this._setOption('placeholder', value);
    }
    /**
     * Specifies whether the editor is read-only.
    
     */
    get readOnly() {
      return this._getOption('readOnly');
    }
    set readOnly(value) {
      this._setOption('readOnly', value);
    }
    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    get rtlEnabled() {
      return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value) {
      this._setOption('rtlEnabled', value);
    }
    /**
     * Specifies whether to display the Clear button in the UI component.
    
     */
    get showClearButton() {
      return this._getOption('showClearButton');
    }
    set showClearButton(value) {
      this._setOption('showClearButton', value);
    }
    /**
     * Specifies whether to show the buttons that change the value by a step.
    
     */
    get showSpinButtons() {
      return this._getOption('showSpinButtons');
    }
    set showSpinButtons(value) {
      this._setOption('showSpinButtons', value);
    }
    /**
     * Specifies how much the UI component&apos;s value changes when using the spin buttons, Up/Down arrow keys, or mouse wheel.
    
     */
    get step() {
      return this._getOption('step');
    }
    set step(value) {
      this._setOption('step', value);
    }
    /**
     * Specifies how the UI component&apos;s text field is styled.
    
     */
    get stylingMode() {
      return this._getOption('stylingMode');
    }
    set stylingMode(value) {
      this._setOption('stylingMode', value);
    }
    /**
     * Specifies the number of the element when the Tab key is used for navigating.
    
     */
    get tabIndex() {
      return this._getOption('tabIndex');
    }
    set tabIndex(value) {
      this._setOption('tabIndex', value);
    }
    /**
     * The read-only property that holds the text displayed by the UI component input element.
    
     */
    get text() {
      return this._getOption('text');
    }
    set text(value) {
      this._setOption('text', value);
    }
    /**
     * Specifies whether to use touch friendly spin buttons. Applies only if showSpinButtons is true.
    
     */
    get useLargeSpinButtons() {
      return this._getOption('useLargeSpinButtons');
    }
    set useLargeSpinButtons(value) {
      this._setOption('useLargeSpinButtons', value);
    }
    /**
     * Information on the broken validation rule. Contains the first item from the validationErrors array.
    
     */
    get validationError() {
      return this._getOption('validationError');
    }
    set validationError(value) {
      this._setOption('validationError', value);
    }
    /**
     * An array of the validation rules that failed.
    
     */
    get validationErrors() {
      return this._getOption('validationErrors');
    }
    set validationErrors(value) {
      this._setOption('validationErrors', value);
    }
    /**
     * Specifies how the message about the validation rules that are not satisfied by this editor&apos;s value is displayed.
    
     */
    get validationMessageMode() {
      return this._getOption('validationMessageMode');
    }
    set validationMessageMode(value) {
      this._setOption('validationMessageMode', value);
    }
    /**
     * Specifies the position of a validation message relative to the component. The validation message describes the validation rules that this component&apos;s value does not satisfy.
    
     */
    get validationMessagePosition() {
      return this._getOption('validationMessagePosition');
    }
    set validationMessagePosition(value) {
      this._setOption('validationMessagePosition', value);
    }
    /**
     * Indicates or specifies the current validation status.
    
     */
    get validationStatus() {
      return this._getOption('validationStatus');
    }
    set validationStatus(value) {
      this._setOption('validationStatus', value);
    }
    /**
     * The current number box value.
    
     */
    get value() {
      return this._getOption('value');
    }
    set value(value) {
      this._setOption('value', value);
    }
    /**
     * Specifies the DOM events after which the UI component&apos;s value should be updated.
    
     */
    get valueChangeEvent() {
      return this._getOption('valueChangeEvent');
    }
    set valueChangeEvent(value) {
      this._setOption('valueChangeEvent', value);
    }
    /**
     * Specifies whether the UI component is visible.
    
     */
    get visible() {
      return this._getOption('visible');
    }
    set visible(value) {
      this._setOption('visible', value);
    }
    /**
     * Specifies the UI component&apos;s width.
    
     */
    get width() {
      return this._getOption('width');
    }
    set width(value) {
      this._setOption('width', value);
    }
    /**
    
     * A function that is executed when the UI component loses focus after the text field&apos;s content was changed using the keyboard.
    
    
     */
    onChange;
    /**
    
     * A function that is executed when the UI component is rendered and each time the component is repainted.
    
    
     */
    onContentReady;
    /**
    
     * A function that is executed when the UI component&apos;s input has been copied.
    
    
     */
    onCopy;
    /**
    
     * A function that is executed when the UI component&apos;s input has been cut.
    
    
     */
    onCut;
    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    onDisposing;
    /**
    
     * A function that is executed when the Enter key has been pressed while the UI component is focused.
    
    
     */
    onEnterKey;
    /**
    
     * A function that is executed when the UI component gets focus.
    
    
     */
    onFocusIn;
    /**
    
     * A function that is executed when the UI component loses focus.
    
    
     */
    onFocusOut;
    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    onInitialized;
    /**
    
     * A function that is executed each time the UI component&apos;s input is changed while the UI component is focused.
    
    
     */
    onInput;
    /**
    
     * A function that is executed when a user is pressing a key on the keyboard.
    
    
     */
    onKeyDown;
    /**
    
     * A function that is executed when a user releases a key on the keyboard.
    
    
     */
    onKeyUp;
    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    onOptionChanged;
    /**
    
     * A function that is executed when the UI component&apos;s input has been pasted.
    
    
     */
    onPaste;
    /**
    
     * A function that is executed after the UI component&apos;s value is changed.
    
    
     */
    onValueChanged;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    accessKeyChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    activeStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    buttonsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    disabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    elementAttrChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    focusStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    formatChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    heightChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hintChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hoverStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    inputAttrChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    invalidValueMessageChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    isDirtyChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    isValidChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    labelChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    labelModeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    maxChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    minChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    modeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    nameChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    placeholderChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    readOnlyChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    rtlEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    showClearButtonChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    showSpinButtonsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    stepChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    stylingModeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    tabIndexChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    textChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    useLargeSpinButtonsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationErrorChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationErrorsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationMessageModeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationMessagePositionChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationStatusChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    valueChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    valueChangeEventChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    visibleChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    widthChange;
    /**
    
     * 
    
    
     */
    onBlur;
    change(_) {}
    touched = _ => {};
    get buttonsChildren() {
      return this._getOption('buttons');
    }
    set buttonsChildren(value) {
      this.setChildren('buttons', value);
    }
    constructor(elementRef, ngZone, templateHost, _watcherHelper, _idh, optionHost, transferState, platformId) {
      super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);
      this._watcherHelper = _watcherHelper;
      this._idh = _idh;
      this._createEventEmitters([{
        subscribe: 'change',
        emit: 'onChange'
      }, {
        subscribe: 'contentReady',
        emit: 'onContentReady'
      }, {
        subscribe: 'copy',
        emit: 'onCopy'
      }, {
        subscribe: 'cut',
        emit: 'onCut'
      }, {
        subscribe: 'disposing',
        emit: 'onDisposing'
      }, {
        subscribe: 'enterKey',
        emit: 'onEnterKey'
      }, {
        subscribe: 'focusIn',
        emit: 'onFocusIn'
      }, {
        subscribe: 'focusOut',
        emit: 'onFocusOut'
      }, {
        subscribe: 'initialized',
        emit: 'onInitialized'
      }, {
        subscribe: 'input',
        emit: 'onInput'
      }, {
        subscribe: 'keyDown',
        emit: 'onKeyDown'
      }, {
        subscribe: 'keyUp',
        emit: 'onKeyUp'
      }, {
        subscribe: 'optionChanged',
        emit: 'onOptionChanged'
      }, {
        subscribe: 'paste',
        emit: 'onPaste'
      }, {
        subscribe: 'valueChanged',
        emit: 'onValueChanged'
      }, {
        emit: 'accessKeyChange'
      }, {
        emit: 'activeStateEnabledChange'
      }, {
        emit: 'buttonsChange'
      }, {
        emit: 'disabledChange'
      }, {
        emit: 'elementAttrChange'
      }, {
        emit: 'focusStateEnabledChange'
      }, {
        emit: 'formatChange'
      }, {
        emit: 'heightChange'
      }, {
        emit: 'hintChange'
      }, {
        emit: 'hoverStateEnabledChange'
      }, {
        emit: 'inputAttrChange'
      }, {
        emit: 'invalidValueMessageChange'
      }, {
        emit: 'isDirtyChange'
      }, {
        emit: 'isValidChange'
      }, {
        emit: 'labelChange'
      }, {
        emit: 'labelModeChange'
      }, {
        emit: 'maxChange'
      }, {
        emit: 'minChange'
      }, {
        emit: 'modeChange'
      }, {
        emit: 'nameChange'
      }, {
        emit: 'placeholderChange'
      }, {
        emit: 'readOnlyChange'
      }, {
        emit: 'rtlEnabledChange'
      }, {
        emit: 'showClearButtonChange'
      }, {
        emit: 'showSpinButtonsChange'
      }, {
        emit: 'stepChange'
      }, {
        emit: 'stylingModeChange'
      }, {
        emit: 'tabIndexChange'
      }, {
        emit: 'textChange'
      }, {
        emit: 'useLargeSpinButtonsChange'
      }, {
        emit: 'validationErrorChange'
      }, {
        emit: 'validationErrorsChange'
      }, {
        emit: 'validationMessageModeChange'
      }, {
        emit: 'validationMessagePositionChange'
      }, {
        emit: 'validationStatusChange'
      }, {
        emit: 'valueChange'
      }, {
        emit: 'valueChangeEventChange'
      }, {
        emit: 'visibleChange'
      }, {
        emit: 'widthChange'
      }, {
        emit: 'onBlur'
      }]);
      this._idh.setHost(this);
      optionHost.setHost(this);
    }
    _createInstance(element, options) {
      return new DxNumberBox(element, options);
    }
    writeValue(value) {
      this.eventHelper.lockedValueChangeEvent = true;
      this.value = value;
      this.eventHelper.lockedValueChangeEvent = false;
    }
    setDisabledState(isDisabled) {
      this.disabled = isDisabled;
    }
    registerOnChange(fn) {
      this.change = fn;
    }
    registerOnTouched(fn) {
      this.touched = fn;
    }
    _createWidget(element) {
      super._createWidget(element);
      this.instance.on('focusOut', e => {
        this.eventHelper.fireNgEvent('onBlur', [e]);
      });
    }
    ngOnDestroy() {
      this._destroyWidget();
    }
    ngOnChanges(changes) {
      super.ngOnChanges(changes);
      this.setupChanges('buttons', changes);
      this.setupChanges('validationErrors', changes);
    }
    setupChanges(prop, changes) {
      if (!(prop in this._optionsToUpdate)) {
        this._idh.setup(prop, changes);
      }
    }
    ngDoCheck() {
      this._idh.doCheck('buttons');
      this._idh.doCheck('validationErrors');
      this._watcherHelper.checkWatchers();
      super.ngDoCheck();
      super.clearChangedOptions();
    }
    _setOption(name, value) {
      let isSetup = this._idh.setupSingle(name, value);
      let isChanged = this._idh.getChanges(name, value) !== null;
      if (isSetup || isChanged) {
        super._setOption(name, value);
      }
    }
    /** @nocollapse */
    static ɵfac = function DxNumberBoxComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxNumberBoxComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1.DxTemplateHost), i0.ɵɵdirectiveInject(i1.WatcherHelper), i0.ɵɵdirectiveInject(i1.IterableDifferHelper), i0.ɵɵdirectiveInject(i1.NestedOptionHost), i0.ɵɵdirectiveInject(i0.TransferState), i0.ɵɵdirectiveInject(PLATFORM_ID));
    };
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: DxNumberBoxComponent,
      selectors: [["dx-number-box"]],
      contentQueries: function DxNumberBoxComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, DxiButtonComponent, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.buttonsChildren = _t);
        }
      },
      hostBindings: function DxNumberBoxComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("valueChange", function DxNumberBoxComponent_valueChange_HostBindingHandler($event) {
            return ctx.change($event);
          })("onBlur", function DxNumberBoxComponent_onBlur_HostBindingHandler($event) {
            return ctx.touched($event);
          });
        }
      },
      inputs: {
        accessKey: "accessKey",
        activeStateEnabled: "activeStateEnabled",
        buttons: "buttons",
        disabled: "disabled",
        elementAttr: "elementAttr",
        focusStateEnabled: "focusStateEnabled",
        format: "format",
        height: "height",
        hint: "hint",
        hoverStateEnabled: "hoverStateEnabled",
        inputAttr: "inputAttr",
        invalidValueMessage: "invalidValueMessage",
        isDirty: "isDirty",
        isValid: "isValid",
        label: "label",
        labelMode: "labelMode",
        max: "max",
        min: "min",
        mode: "mode",
        name: "name",
        placeholder: "placeholder",
        readOnly: "readOnly",
        rtlEnabled: "rtlEnabled",
        showClearButton: "showClearButton",
        showSpinButtons: "showSpinButtons",
        step: "step",
        stylingMode: "stylingMode",
        tabIndex: "tabIndex",
        text: "text",
        useLargeSpinButtons: "useLargeSpinButtons",
        validationError: "validationError",
        validationErrors: "validationErrors",
        validationMessageMode: "validationMessageMode",
        validationMessagePosition: "validationMessagePosition",
        validationStatus: "validationStatus",
        value: "value",
        valueChangeEvent: "valueChangeEvent",
        visible: "visible",
        width: "width"
      },
      outputs: {
        onChange: "onChange",
        onContentReady: "onContentReady",
        onCopy: "onCopy",
        onCut: "onCut",
        onDisposing: "onDisposing",
        onEnterKey: "onEnterKey",
        onFocusIn: "onFocusIn",
        onFocusOut: "onFocusOut",
        onInitialized: "onInitialized",
        onInput: "onInput",
        onKeyDown: "onKeyDown",
        onKeyUp: "onKeyUp",
        onOptionChanged: "onOptionChanged",
        onPaste: "onPaste",
        onValueChanged: "onValueChanged",
        accessKeyChange: "accessKeyChange",
        activeStateEnabledChange: "activeStateEnabledChange",
        buttonsChange: "buttonsChange",
        disabledChange: "disabledChange",
        elementAttrChange: "elementAttrChange",
        focusStateEnabledChange: "focusStateEnabledChange",
        formatChange: "formatChange",
        heightChange: "heightChange",
        hintChange: "hintChange",
        hoverStateEnabledChange: "hoverStateEnabledChange",
        inputAttrChange: "inputAttrChange",
        invalidValueMessageChange: "invalidValueMessageChange",
        isDirtyChange: "isDirtyChange",
        isValidChange: "isValidChange",
        labelChange: "labelChange",
        labelModeChange: "labelModeChange",
        maxChange: "maxChange",
        minChange: "minChange",
        modeChange: "modeChange",
        nameChange: "nameChange",
        placeholderChange: "placeholderChange",
        readOnlyChange: "readOnlyChange",
        rtlEnabledChange: "rtlEnabledChange",
        showClearButtonChange: "showClearButtonChange",
        showSpinButtonsChange: "showSpinButtonsChange",
        stepChange: "stepChange",
        stylingModeChange: "stylingModeChange",
        tabIndexChange: "tabIndexChange",
        textChange: "textChange",
        useLargeSpinButtonsChange: "useLargeSpinButtonsChange",
        validationErrorChange: "validationErrorChange",
        validationErrorsChange: "validationErrorsChange",
        validationMessageModeChange: "validationMessageModeChange",
        validationMessagePositionChange: "validationMessagePositionChange",
        validationStatusChange: "validationStatusChange",
        valueChange: "valueChange",
        valueChangeEventChange: "valueChangeEventChange",
        visibleChange: "visibleChange",
        widthChange: "widthChange",
        onBlur: "onBlur"
      },
      standalone: false,
      features: [i0.ɵɵProvidersFeature([DxTemplateHost, WatcherHelper, CUSTOM_VALUE_ACCESSOR_PROVIDER, NestedOptionHost, IterableDifferHelper]), i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
      decls: 0,
      vars: 0,
      template: function DxNumberBoxComponent_Template(rf, ctx) {},
      encapsulation: 2
    });
  }
  return DxNumberBoxComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DxNumberBoxModule = /*#__PURE__*/(() => {
  class DxNumberBoxModule {
    /** @nocollapse */static ɵfac = function DxNumberBoxModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxNumberBoxModule)();
    };
    /** @nocollapse */
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DxNumberBoxModule
    });
    /** @nocollapse */
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [DxiButtonModule, DxoOptionsModule, DxoFormatModule, DxIntegrationModule, DxTemplateModule, DxiButtonModule, DxoOptionsModule, DxoFormatModule, DxTemplateModule]
    });
  }
  return DxNumberBoxModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DxNumberBoxComponent, DxNumberBoxModule };
